import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "api/apiClient.ts";

// Define Mosque Type
interface Mosque {
  _id?: string;
  name: string;
  address: string;
  location: {
    type: string;
    coordinates: number[];
  };
}

// Fetch Mosques function
const fetchMosques = async (): Promise<Mosque[]> => {
  const { data } = await apiClient.get("/mosques");

  if (data.status !== "success" || !Array.isArray(data.data.mosques)) {
    throw new Error("Invalid response format");
  }

  return data.data.mosques;
};

// Hook to get Mosques
export const useMosques = () => {
  return useQuery({
    queryKey: ["mosques"],
    queryFn: fetchMosques,
  });
};

// Add Mosque Hook
export const useAddMosque = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mosque: Mosque) => {
      await apiClient.post("/mosques/create", mosque);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mosques"] });
    },
  });
};

// Delete Mosque by ID Hook
export const useDeleteMosque = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete(`/mosques/${id}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mosques"] });
    },
  });
};

// Delete Multiple Mosques Hook
export const useDeleteMosques = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data } = await apiClient.delete("/mosques", {
        data: { ids },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mosques"] });
    },
  });
};
